import isEmpty from 'lodash/isEmpty';

import {ALL_CATEGORIES_GUID} from '@constants/categories';
import {EVENT_PROVIDER, EVENT_PROVIDER_DISPLAY_NAMES} from '@constants/event-providers';
import {WORLDWIDE_ID} from '@constants/locations';

const CRISIS24_PROVIDERS = [EVENT_PROVIDER.INTERNAL_CURATED, EVENT_PROVIDER.INTERNAL_AUTOMATED];

function getSelectedIds(items) {
  return (Array.isArray(items) ? items : []).map((item) => item?.value ?? item);
}

export function getAlertPreferenceSelectValues(providers, isEditing) {
  const selectValues = getProviderFilterSelectValues(providers);

  return isEditing && !selectValues.length ? ['CRISIS24'] : selectValues;
}

export function getFieldsToClearWhenDataminrSelected({categories, locations, usableCategoryIds = []} = {}) {
  const categoryIds = getSelectedIds(categories);
  const hasAllCategories =
    categoryIds.includes(ALL_CATEGORIES_GUID) ||
    (usableCategoryIds.length > 0 && usableCategoryIds.every((id) => categoryIds.includes(id)));

  return {
    ...(hasAllCategories && {categories: []}),
    ...(getSelectedIds(locations).includes(WORLDWIDE_ID) && {locations: []}),
  };
}

function allIdsSelected(selected, requiredIds = []) {
  return requiredIds.length > 0 && requiredIds.every((id) => selected.includes(id));
}

export function getFieldsToConvertWhenDataminrRemoved({
  categories,
  locations,
  topLevelCategoryIds = [],
  topLevelLocationIds = [],
} = {}) {
  return {
    ...(allIdsSelected(getSelectedIds(categories), topLevelCategoryIds) && {
      categories: [{value: ALL_CATEGORIES_GUID, label: 'All'}],
    }),
    ...(allIdsSelected(getSelectedIds(locations), topLevelLocationIds) && {
      locations: [{value: WORLDWIDE_ID, label: 'Worldwide'}],
    }),
  };
}

export const PROVIDER_FILTER_OPTIONS = [
  {
    key: 'CRISIS24',
    label: 'Crisis24',
    providers: CRISIS24_PROVIDERS,
  },
  {
    key: EVENT_PROVIDER.DATAMINR,
    label: EVENT_PROVIDER_DISPLAY_NAMES[EVENT_PROVIDER.DATAMINR],
    providers: [EVENT_PROVIDER.DATAMINR],
  },
];

export const PROVIDER_FILTER_SELECT_OPTIONS = PROVIDER_FILTER_OPTIONS.map(({key, label}) => ({
  value: key,
  label,
}));

function isProviderFilterOptionSelected(option, selectedProviders = []) {
  return option.providers.some((provider) => selectedProviders.includes(provider));
}

export function getProviderFilterSelectValues(providers = []) {
  return PROVIDER_FILTER_OPTIONS.filter((option) => isProviderFilterOptionSelected(option, providers || [])).map(
    (option) => option.key,
  );
}

export function parseProviderFilterSelectValues(selectValues = []) {
  return [
    ...new Set(
      PROVIDER_FILTER_OPTIONS.filter((option) => selectValues.includes(option.key)).flatMap(
        (option) => option.providers,
      ),
    ),
  ];
}

export function resolveProvidersForApi(providers, hasDataminrAlerts) {
  const allProviders = PROVIDER_FILTER_OPTIONS.flatMap((option) => option.providers);

  if (!hasDataminrAlerts) {
    return [...CRISIS24_PROVIDERS];
  }

  if (!providers?.length) {
    return [...allProviders];
  }

  return providers;
}

export function getEventProviderDisplayName(provider) {
  return EVENT_PROVIDER_DISPLAY_NAMES[provider] ?? provider;
}

export function formatEventProviders(providers) {
  if (!Array.isArray(providers) || isEmpty(providers)) {
    return '';
  }

  return [...new Set(providers.map(getEventProviderDisplayName))].join(', ');
}

export function getProviderFilterOptionCount(providers = []) {
  return getProviderFilterSelectValues(providers).length;
}

/**
 * Returns true when the alert's providers are known and none are internal (automated or curated).
 * Returns false while alertDetails or providers is not yet available, preventing premature data fetching.
 */
export function isExternalProvider(alertDetails) {
  return !!alertDetails?.providers && !alertDetails.providers.some((item) => CRISIS24_PROVIDERS.includes(item));
}
